import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useCollection } from "../lib/useCollection";
import { resolveMediaUrl } from "../lib/mediaUrl";

const LAYOUTS = [
    { span: "lg:col-span-7 lg:row-span-2", height: "h-[420px] lg:h-[640px]" },
    { span: "lg:col-span-5", height: "h-[300px]" },
    { span: "lg:col-span-5", height: "h-[320px]" },
    { span: "lg:col-span-7", height: "h-[320px]" },
    { span: "lg:col-span-12", height: "h-[360px]" },
];

export const WORKS = [
    {
        id: "luxe-coffee-brand",
        title: "Luxe Coffee Co.",
        category: "Brand Identity",
        image: "https://images.pexels.com/photos/8489947/pexels-photo-8489947.jpeg",
        span: "lg:col-span-7 lg:row-span-2",
        height: "h-[420px] lg:h-[640px]",
    },
    {
        id: "minimal-card-system",
        title: "Atelier · Stationery",
        category: "Print & Identity",
        image:
            "https://images.unsplash.com/photo-1707214520187-45fc42b82bdc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxicmFuZGluZyUyMG1vY2t1cCUyMGRhcmt8ZW58MHx8fHwxNzc3MjE0MDUwfDA&ixlib=rb-4.1.0&q=85",
        span: "lg:col-span-5",
        height: "h-[300px]",
    },
    {
        id: "neon-glow-signage",
        title: "Neon Glow Signage",
        category: "Glow Sign Board",
        image:
            "https://images.unsplash.com/photo-1557044829-d613e848004a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxuZW9uJTIwc2lnbiUyMGJvYXJkJTIwbmlnaHR8ZW58MHx8fHwxNzc3MjE0MDUwfDA&ixlib=rb-4.1.0&q=85",
        span: "lg:col-span-5",
        height: "h-[320px]",
    },
    {
        id: "crafted-packaging",
        title: "Crafted · Packaging",
        category: "Packaging Design",
        image:
            "https://images.unsplash.com/photo-1677753669144-66e809db63e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwYWNrYWdpbmclMjBkZXNpZ24lMjBtb2NrdXB8ZW58MHx8fHwxNzc3MjE0MDUwfDA&ixlib=rb-4.1.0&q=85",
        span: "lg:col-span-7",
        height: "h-[320px]",
    },
    {
        id: "corporate-kit",
        title: "Corporate · Identity Kit",
        category: "Brand Identity",
        image: "https://images.pexels.com/photos/8489951/pexels-photo-8489951.jpeg",
        span: "lg:col-span-12",
        height: "h-[360px]",
    },
];

export const FeaturedWork = ({ id = "work", limit, showHeading = true, showCta = true }) => {
    const portfolio = useCollection("/portfolio", []);
    const items = portfolio.length
        ? portfolio.slice(0, limit || 5).map((p, i) => ({
              id: p.id,
              title: p.title,
              category: p.category,
              image: resolveMediaUrl(p.image_url),
              link: `/portfolio/${p.id}`,
              ...LAYOUTS[i % LAYOUTS.length],
          }))
        : (limit ? WORKS.slice(0, limit) : WORKS).map((w) => ({ ...w, link: "/portfolio" }));
    return (
        <section
            id={id}
            data-testid="featured-work-section"
            className="relative py-24 sm:py-32 bg-[#050505] border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {showHeading && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.6 }}
                        className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
                    >
                        <div>
                            <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-4">
                                Featured Work
                            </div>
                            <h2 className="font-poppins font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
                                Selected work from the studio.
                            </h2>
                        </div>
                        {showCta && (
                            <Link
                                to="/portfolio"
                                data-testid="featured-view-all-link"
                                className="self-start md:self-end inline-flex items-center gap-2 font-inter text-sm text-zinc-300 hover:text-gold transition-colors"
                            >
                                View full portfolio
                                <ArrowUpRight size={16} />
                            </Link>
                        )}
                    </motion.div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 auto-rows-min">
                    {items.map((w, i) => (
                        <motion.div
                            key={w.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.6, delay: i * 0.06 }}
                            data-testid={`work-tile-${w.id}`}
                            className={`relative group rounded-2xl overflow-hidden bg-[#0c0c0c] border border-white/5 hover:border-gold/30 transition-colors ${w.span || ""} ${w.height || "h-[320px]"}`}
                        >
                            <Link to={w.link} className="absolute inset-0 block" data-testid={`work-link-${w.id}`}>
                                <img
                                    src={w.image}
                                    alt={`${w.title} — ${w.category} by Raj Graphics`}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-700 ease-out"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-90 group-hover:opacity-95 transition-opacity" />
                                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
                                    <div className="font-inter text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                                        {w.category}
                                    </div>
                                    <div className="flex items-end justify-between gap-4">
                                        <h3 className="font-poppins font-medium text-white text-xl md:text-2xl">
                                            {w.title}
                                        </h3>
                                        <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-colors">
                                            <ArrowUpRight
                                                size={16}
                                                className="text-white group-hover:text-black transition-colors"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default FeaturedWork;
